import { buildPath, paths } from 'url-registry';

/**
 * In-app URL used as `fromPage` on full `/share-transcript` links for logged-in users.
 *
 * Short links (e.g. ATS fields with a 255-char cap) omit `fromPage`; ShareLinkRoute falls
 * back to this URL so behavior matches long links without duplicating the query string.
 *
 * Must stay aligned with `from_page` in
 * `server/call_results_template/call_results_helper.py` (`get_web_link_for_candidate`).
 */
export function defaultFromPageForShareTranscriptLink(
  origin: string,
  candidateId: string,
  campaignId: string
): string {
  const base = origin.replace(/\/$/, '');
  return `${base}${buildPath(
    paths.JOBS_EDITOR,
    { campaign_id: campaignId },
    { tab: 'candidates', candidateId }
  )}`;
}

/**
 * Build `fromPage` so a logged-in redirect opens this candidate's transcript.
 *
 * Prefer the current page (folder path, filters, tab) but always ensure
 * `candidateId` is present — nested drawers (e.g. multi-candidate chat) can
 * open a transcript without writing `candidateId` into the URL.
 */
export function buildFromPageForShareTranscriptLink(
  origin: string,
  candidateId: string,
  campaignId: string,
  currentHref: string
): string {
  try {
    const url = new URL(currentHref);
    url.searchParams.set('candidateId', candidateId);
    // Interviews / notifications open the drawer via selectedCandidateId.
    if (
      url.searchParams.has('selectedCandidateId') ||
      url.pathname.startsWith('/interviews') ||
      url.pathname.startsWith('/notifications')
    ) {
      url.searchParams.set('selectedCandidateId', candidateId);
      url.searchParams.set('selectedCampaignId', campaignId);
    }
    return url.toString();
  } catch {
    return defaultFromPageForShareTranscriptLink(
      origin,
      candidateId,
      campaignId
    );
  }
}

/**
 * Full `/share-transcript` URL copied from the Share modal for logged-in users.
 */
export function buildShareTranscriptCopyUrl(
  origin: string,
  candidateId: string,
  campaignId: string,
  currentHref: string
): string {
  const base = origin.replace(/\/$/, '');
  const fromPage = buildFromPageForShareTranscriptLink(
    origin,
    candidateId,
    campaignId,
    currentHref
  );
  return `${base}${buildPath(paths.SHARE_TRANSCRIPT, undefined, {
    candidateId,
    campaignId,
    fromPage,
  })}`;
}
